import React from 'react';
import { usePermissions } from '@/components/hooks/usePermissions';

/**
 * Componente guard que renderiza children solo si el usuario tiene los permisos requeridos
 * 
 * @param {string|string[]} permission - Código de permiso o array de códigos
 * @param {boolean} requireAll - Si true, requiere todos los permisos del array
 * @param {React.ReactNode} fallback - Contenido a mostrar si no tiene permiso
 * @param {React.ReactNode} children - Contenido a mostrar si tiene permiso
 */
export function PermissionGuard({ 
  permission, 
  requireAll = false, 
  fallback = null, 
  children 
}) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = usePermissions();

  // Mientras carga, no mostrar nada para evitar flash
  if (isLoading) {
    return null;
  }

  // Verificar permisos
  let hasAccess = false;

  if (!permission) {
    hasAccess = true;
  } else if (Array.isArray(permission)) {
    hasAccess = requireAll 
      ? hasAllPermissions(permission) 
      : hasAnyPermission(permission);
  } else {
    hasAccess = hasPermission(permission);
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * HOC para proteger componentes completos
 */
export function withPermission(WrappedComponent, permission, fallback = null) {
  return function PermissionProtectedComponent(props) {
    return (
      <PermissionGuard permission={permission} fallback={fallback}>
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
}

export default PermissionGuard;